/**
 * Uitbetaling na afsluiting van een evenement (deel 3, hoofdstuk 2).
 *
 * De volgorde ligt vast en staat ook zo op het verkoopoverzicht dat de kraam
 * krijgt, zodat hij kan controleren wat wij namens hem hebben opgemaakt:
 *
 *   bruto omzet
 *   - retouren
 *   +/- correcties
 *   = grondslag
 *   - commissie organisator
 *   - doorbelaste transactiekosten
 *   - inhoudingen (bijvoorbeeld niet ingeleverde apparatuur)
 *   = subtotaal
 *   - buffer (wordt na X dagen nabetaald)
 *   = nu uit te betalen
 *
 * Statiegeld staat er bewust buiten: het is geen omzet (regel 10) en telt niet
 * mee in de commissie. Het saldo komt als aparte post binnen via
 * `statiegeldSaldoCent` en blijft bij wie de kraam of bar draait — zie
 * `uitbater`.
 *
 * De doorbelaste transactiekosten van de betaalpartij gaan hier van de omzet
 * af. Ze staan niet op de factuur aan de organisator: het zijn kosten van de
 * verkoop, niet van de kraamset.
 */

import { berekenCommissie, type CommissieResultaat } from './commissie.js';
import { percentageVan, sommeer, type Basispunten, type Cent } from './geld.js';

/**
 * Wie draait de kraam of bar. Draait de organisator een bar in eigen beheer,
 * dan gaat de hele afrekening van die bar — inclusief het statiegeld dat niet
 * is ingeleverd — naar de organisator in plaats van naar een kraamhouder.
 */
export type Uitbater = 'kraam' | 'organisator';

export interface Afrekeningregel {
  omschrijving: string;
  bedragCent: Cent;
}

export interface UitbetalingKraamInvoer {
  /** Producten inclusief BTW, zonder statiegeld. */
  brutoOmzetCent: Cent;
  /** Terugbetaald aan klanten, als positief getal. */
  retourenCent?: Cent;
  /** Correcties op afgeronde bonnen: positief bijbetaald, negatief terugbetaald. */
  correctiesCent?: Cent;
  /** Saldo statiegeld: uitgegeven borg minus terugbetaalde borg. */
  statiegeldSaldoCent?: Cent;
  commissieBasispunten: Basispunten;
  /** Doorbelaste transactiekosten, als positief getal. */
  transactiekostenCent?: Cent;
  /** Overige inhoudingen, als positief getal. */
  inhoudingenCent?: Cent;
  /** Buffer voor terugboekingen, in basispunten over het subtotaal. */
  bufferBasispunten?: Basispunten;
  /** Wie de kraam draait. Standaard de kraam zelf. */
  uitbater?: Uitbater;
}

export interface UitbetalingKraam {
  /** Naar wie het geld van deze kraam gaat. */
  ontvanger: Uitbater;
  regels: Afrekeningregel[];
  commissie: CommissieResultaat;
  grondslagCent: Cent;
  subtotaalCent: Cent;
  bufferCent: Cent;
  nuUitTeBetalenCent: Cent;
  /** Wat na de bufferperiode alsnog wordt overgemaakt. */
  naBufferUitTeBetalenCent: Cent;
  /** Alles bij elkaar, dus subtotaal. Handig voor de controle. */
  totaalUitTeBetalenCent: Cent;
}

export function berekenUitbetalingKraam(invoer: UitbetalingKraamInvoer): UitbetalingKraam {
  const retouren = invoer.retourenCent ?? 0;
  const correcties = invoer.correctiesCent ?? 0;
  const statiegeldSaldo = invoer.statiegeldSaldoCent ?? 0;
  const transactiekosten = invoer.transactiekostenCent ?? 0;
  const inhoudingen = invoer.inhoudingenCent ?? 0;
  const bufferBasispunten = invoer.bufferBasispunten ?? 0;

  for (const [naam, waarde] of [
    ['retouren', retouren],
    ['transactiekosten', transactiekosten],
    ['inhoudingen', inhoudingen],
  ] as const) {
    if (waarde < 0) {
      throw new Error(`${naam} moet als positief bedrag worden aangeleverd, kreeg ${waarde}`);
    }
  }
  if (bufferBasispunten < 0 || bufferBasispunten > 10_000) {
    throw new Error(`buffer moet tussen 0 en 10000 basispunten liggen: ${bufferBasispunten}`);
  }

  const commissie = berekenCommissie(
    {
      brutoOmzetCent: invoer.brutoOmzetCent,
      retourenCent: retouren,
      correctiesCent: correcties,
    },
    invoer.commissieBasispunten,
  );

  const grondslagCent = commissie.grondslagCent;

  const subtotaalCent = sommeer([
    grondslagCent,
    statiegeldSaldo,
    -commissie.commissieCent,
    -transactiekosten,
    -inhoudingen,
  ]);

  // Op een negatief subtotaal houd je niets in: dan is er niets uit te betalen
  // en loopt het via een aparte afspraak, niet via de buffer.
  const bufferCent = subtotaalCent > 0 ? percentageVan(subtotaalCent, bufferBasispunten) : 0;
  const nuUitTeBetalenCent = subtotaalCent - bufferCent;

  const regels: Afrekeningregel[] = [
    { omschrijving: 'Bruto omzet', bedragCent: invoer.brutoOmzetCent },
  ];
  if (retouren !== 0) regels.push({ omschrijving: 'Retouren', bedragCent: -retouren });
  if (correcties !== 0) regels.push({ omschrijving: 'Correcties', bedragCent: correcties });
  if (statiegeldSaldo !== 0) {
    regels.push({ omschrijving: 'Statiegeld (geen omzet)', bedragCent: statiegeldSaldo });
  }
  regels.push({
    omschrijving: `Commissie organisator (${commissie.basispunten / 100}%)`,
    bedragCent: -commissie.commissieCent,
  });
  if (transactiekosten !== 0) {
    regels.push({ omschrijving: 'Transactiekosten', bedragCent: -transactiekosten });
  }
  if (inhoudingen !== 0) {
    regels.push({ omschrijving: 'Inhoudingen', bedragCent: -inhoudingen });
  }
  if (bufferCent !== 0) {
    regels.push({
      omschrijving: `Buffer (${bufferBasispunten / 100}%, wordt nabetaald)`,
      bedragCent: -bufferCent,
    });
  }

  return {
    ontvanger: invoer.uitbater ?? 'kraam',
    regels,
    commissie,
    grondslagCent,
    subtotaalCent,
    bufferCent,
    nuUitTeBetalenCent,
    naBufferUitTeBetalenCent: bufferCent,
    totaalUitTeBetalenCent: subtotaalCent,
  };
}

export interface UitbetalingOrganisatorInvoer {
  /** Alle commissie over alle kramen bij elkaar. */
  commissieTotaalCent: Cent;
  /**
   * Onze factuur aan de organisator, inclusief BTW: de kraamsets per dag.
   * Transactiekosten horen hier NIET in — die gaan al van de omzet van de
   * kraam af. Ze twee keer meenemen incasseert ze dubbel.
   */
  onzeFactuurBrutoCent?: Cent;
}

export interface UitbetalingOrganisator {
  regels: Afrekeningregel[];
  uitTeBetalenCent: Cent;
  /** Bij een negatief bedrag moet de organisator bijbetalen in plaats van ontvangen. */
  organisatorMoetBijbetalen: boolean;
}

export function berekenUitbetalingOrganisator(
  invoer: UitbetalingOrganisatorInvoer,
): UitbetalingOrganisator {
  const factuur = invoer.onzeFactuurBrutoCent ?? 0;
  if (factuur < 0) {
    throw new Error(`onze factuur moet als positief bedrag worden aangeleverd, kreeg ${factuur}`);
  }
  const uitTeBetalenCent = sommeer([invoer.commissieTotaalCent, -factuur]);

  const regels: Afrekeningregel[] = [
    { omschrijving: 'Commissie over kraamomzet', bedragCent: invoer.commissieTotaalCent },
  ];
  if (factuur !== 0) {
    regels.push({ omschrijving: 'Kraamsets', bedragCent: -factuur });
  }

  return {
    regels,
    uitTeBetalenCent,
    organisatorMoetBijbetalen: uitTeBetalenCent < 0,
  };
}

export interface AfrekeningControle {
  totaalUitCent: Cent;
  verschilCent: Cent;
  /** Alleen bij verschil nul mag het SEPA-bestand de deur uit. */
  sluit: boolean;
}

/**
 * Het controlescherm uit fase 9: totaal binnengekomen tegenover totaal uit te
 * betalen. Verschil moet nul zijn, anders gaat er niets naar de bank.
 * De buffer telt hier mee als uit te betalen — hij is alleen nog niet betaald.
 */
export function controleerAfrekening(
  ontvangenCent: Cent,
  posten: readonly Afrekeningregel[],
): AfrekeningControle {
  const totaalUitCent = sommeer(posten.map((post) => post.bedragCent));
  const verschilCent = ontvangenCent - totaalUitCent;
  return { totaalUitCent, verschilCent, sluit: verschilCent === 0 };
}
